import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import InputMask from "react-input-mask";
import styled, { css } from "styled-components";
import cn from "classnames";
import { info as infoNotif, error as errorNotif } from "react-notification-system-redux";

import { phoneFormatter, phoneCleaner, phoneNumRegexp } from "_helpers";

import breakpoints from "_constants/breakpoints";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  background-color: #ffffff;
  width: 100%;
  max-width: 660px;
  margin-bottom: 32px;
`;
const Title = styled.div`
  height: 47px;
  margin-left: 32px;
  margin-bottom: 14px;
  font-style: normal;
  font-family: "Montserrat";
  font-weight: bold;
  font-size: 24px;
  line-height: 47px;
  color: #03589c;
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      font-size: 22px;
      text-align: center;

      @media (max-width: ${breakpoints.mobileM}) {
        font-size: 18px;
        margin-left: 0px;
        margin-bottom: 0px;
      }
    `}
`;
const InfoForm = styled(Form)`
  padding: 48px 52px 52px 48px;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      padding: 0 28px;
      box-shadow: none;
      @media (max-width: ${breakpoints.mobileM}) {
        padding: 8px 16px;
      }
    `}
`;
const Group = styled(Form.Group)`
  color: #394149;
  &:not(:last-child) {
    margin-bottom: 18px;
  }
`;
const Label = styled(Form.Label)`
  height: 28px;
  margin-left: 8px;
  margin-bottom: 0;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 28px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
`;
const Control = styled(Form.Control)`
  font-family: "Montserrat";
  color: #394149;
  font-weight: 400;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.75rem;
  }
`;
const Phone = styled(InputMask)`
  display: block;
  width: 100%;
  font-family: "Montserrat";
  font-weight: 400;
  color: #394149;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 14px;
  }
`;
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 660px;
`;
const SaveButton = styled.button`
  width: 100%;
  max-width: 180px;
  height: 52px;
  margin: 0 7px;
  border: none;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  color: #fafaff;
  background-color: #f37217;

  transition: 0.1s ease-in-out;

  &:disabled {
    background-color: #afafaf;
  }

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.825rem;
  }
`;
const CancelButton = styled(SaveButton)`
  border: 2px solid #ecedf1;
  color: #03589c;
  background-color: #ffffff;

  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 0.825rem;
  }
`;

function ContactInfo({ userInfo, onSave }) {
  const dispatch = useDispatch();

  const [user, setUser] = useState({ ...userInfo, phone: phoneCleaner(userInfo.phone) });
  const [validated, setValidated] = useState(false);

  const isDirty = phoneCleaner(userInfo?.phone) !== user?.phone || userInfo?.email !== user?.email;

  const handleChangeUser = ({ target: { name, value } }) => {
    if (name === "phone") {
      setUser({ ...user, [name]: phoneCleaner(value) });
    } else {
      setUser({ ...user, [name]: value });
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();

    setValidated(false);
    setUser({ ...userInfo, phone: phoneFormatter(userInfo.phone) });
    dispatch(infoNotif({ title: "Info", message: "Changes successfuly canceled." }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      dispatch(errorNotif({ title: "Error", message: "Please, enter correct values" }));
    } else {
      onSave({ ...userInfo, ...user });
    }

    setValidated(true);
  };

  // const phoneRegexp = new RegExp(/((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}/);
  const emailRegexp = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i);

  useEffect(() => {
    setUser({ ...userInfo, phone: phoneCleaner(userInfo.phone) });
  }, [userInfo]);

  return (
    <Container>
      <Title>Contact Info</Title>

      <InfoForm noValidate validated={validated} onSubmit={handleSave}>
        <Group>
          <Label>Contact Phone</Label>
          <Phone
            required
            className={cn("form-control", { "is-invalid": !user.phone || !phoneNumRegexp.test(user.phone) })}
            type="text"
            placeholder="Enter contact phone"
            mask="(999) 999-9999"
            pattern="((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}"
            name="phone"
            value={user.phone}
            // isInvalid={!user.phone || !phoneRegexp.test(user.phone)}
            onChange={handleChangeUser}
          />
          <Form.Control.Feedback type="invalid">Please, enter correct Phone Number</Form.Control.Feedback>
        </Group>

        <Group>
          <Label>Email Address</Label>
          <Control
            required
            type="email"
            pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
            placeholder="Enter email address"
            name="email"
            value={user.email}
            isInvalid={!user.email || !emailRegexp.test(user.email)}
            onChange={handleChangeUser}
          />
          <Form.Control.Feedback type="invalid">Please, enter correct Email</Form.Control.Feedback>
        </Group>
        <Buttons>
          <CancelButton type="reset" onClick={handleCancel}>
            Cancel
          </CancelButton>
          <SaveButton type="submit" disabled={!isDirty}>
            Save changes
          </SaveButton>
        </Buttons>
      </InfoForm>
    </Container>
  );
}

ContactInfo.propTypes = {
  userInfo: PropTypes.object,
  onSave: PropTypes.func,
};

export default ContactInfo;
